import { hardRedirectTo } from '../../../utils';
import Button from '../../generic/Button/Button';
import SocialLoginButtons from '../components/SocialLoginButtons';
import UseDifferentAccountButton from '../components/UseDifferentAccountButton';
import { socialLoginEntryUrl, withNext } from '../ssoEntry';
import { STRATEGY_LABEL } from '../strategyLabels';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { AvailableMethod, Candidate, OAuthProvider } from '../../../../shared/types/routes/auth/v2';

import CredentialForm from './CredentialForm';

import styles from '../Login.module.less';

// A stable default so the prop's fallback is not a fresh array literal on every render.
const NO_PROVIDERS: OAuthProvider[] = [];

export type MethodsStepProps = {
  org: Candidate;
  identifier: string;
  next?: string;
  /** The globally-configured social providers, offered when the member's only way in is a linked social account. */
  oauthProviders?: OAuthProvider[];
  onMfaRequired: (mfaMethod: MfaMethodType) => void;
  onBack: () => void;
};

/** A redirect-based SSO method the user is bounced to their IdP for (SAML, or Google OAuth). */
type SsoRedirectMethod = Extract<AvailableMethod, { type: 'saml' } | { type: 'oauth' }>;

const isSsoRedirectMethod = (method: AvailableMethod): method is SsoRedirectMethod =>
  method.type === 'saml' || method.type === 'oauth';

/**
 * The label and V2 entry URL for a redirect-based SSO method, threading the `?next=` deep link. SAML carries its
 * own entry URL from the backend (`samlEntryUrl`, the V2 shadow route); a Google (`oauth`) method resolves to the
 * V2 social entry (`socialLoginEntryUrl`) — never the V1 `/account/{provider}` route.
 */
const ssoRedirect = (method: SsoRedirectMethod, next?: string): { label: string; url: string } =>
  method.type === 'saml'
    ? { label: STRATEGY_LABEL.saml, url: withNext(method.samlEntryUrl, next) }
    : { label: STRATEGY_LABEL[method.provider], url: socialLoginEntryUrl(method.provider, next) };

/**
 * Renders the sign-in method for the org the user picked. Password and LDAP orgs share one credential form (the
 * backend selects the strategy), so it shows for any org with a credential method. An SSO-only org (SAML / Google)
 * instead offers a "Continue with …" button per configured method that redirects the user to that method's IdP.
 * A member with no org-scoped method in an org that doesn't require SSO signed up socially (the password method is
 * omitted for accounts without a usable password), so the global social buttons are offered instead; only a
 * misconfigured SSO-required org falls through to the contact-your-admin message.
 */
const MethodsStep = ({
  org, identifier, next, oauthProviders = NO_PROVIDERS, onMfaRequired, onBack,
}: MethodsStepProps) => {
  const hasCredentialMethod = org.availableMethods.some(
    method => method.type === 'password' || method.type === 'ldap',
  );
  const hasPasswordMethod = org.availableMethods.some(method => method.type === 'password');

  if (hasCredentialMethod) {
    return (
      <CredentialForm
        org={org}
        identifier={identifier}
        // Password wins for a mixed org (LDAP + local): it keeps the forgot-password link and generic copy.
        credentialKind={hasPasswordMethod ? 'password' : 'ldap'}
        showForgotPassword={hasPasswordMethod}
        next={next}
        onMfaRequired={onMfaRequired}
        onBack={onBack}
      />
    );
  }

  const ssoMethods = org.availableMethods.filter(isSsoRedirectMethod);
  const hasSsoMethod = ssoMethods.length > 0;
  // No org-scoped method and no SSO requirement: the member's account has no usable password (they signed
  // up socially), so the way in is the social account they signed up with — limited to the providers this
  // member may actually use for the org, not every globally-configured one.
  const allowedSocialProviders = oauthProviders.filter(provider => org.allowedStrategies.includes(provider));
  const shouldOfferSocialFallback = !hasSsoMethod && !org.requiresSso && allowedSocialProviders.length > 0;

  // An SSO-required org with no configured provider (a misconfiguration) has no usable method, so say so
  // plainly rather than implying an SSO button that will never appear.
  let placeholder = 'No sign-in method is configured for this organization. Contact your administrator.';
  if (hasSsoMethod) {
    placeholder = 'This organization uses single sign-on.';
  } else if (shouldOfferSocialFallback) {
    placeholder = 'This account signs in with a linked social account.';
  }

  return (
    <div className={styles.card}>
      <h1 className={styles.heading}>Sign in to {org.organizationName}</h1>
      <p className={styles.placeholder}>{placeholder}</p>
      {hasSsoMethod && (
        <div className={styles.ssoActions}>
          {ssoMethods.map(method => {
            const { label, url } = ssoRedirect(method, next);
            return (
              <Button
                key={method.type === 'oauth' ? method.provider : method.type}
                isFullWidth
                size="large"
                onClick={() => hardRedirectTo(url)}
              >
                Continue with {label}
              </Button>
            );
          })}
        </div>
      )}
      {shouldOfferSocialFallback && <SocialLoginButtons providers={allowedSocialProviders} next={next} />}
      <UseDifferentAccountButton onClick={onBack} />
    </div>
  );
};

export default MethodsStep;
